import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RotateCcw, AlertTriangle } from "lucide-react";
import { CreateAdModalProps, getPlainTextFromHtml } from "./types";
import { useProfile } from "@/context/ProfileContext";
import { useCreateAd } from "./hooks/useCreateAd";
import { useQualifyingQuestions } from "./hooks/useQualifyingQuestions";
import { trpcClient } from "@/trpc/client";
import { useToast } from "@/hooks/use-toast";
import { storage } from "@/config/firebaseConfig";
import { getDownloadURL, ref } from "firebase/storage";
import StepOne from "./components/StepOne";
import StepTwo from "./components/StepTwo";
import StepThree from "./components/StepThree";
import StepFour from "./components/StepFour";
import PreviewDialog from "./components/PreviewDialog";
import EditDescriptionDialog from "./components/EditDescriptionDialog";
import { LogoPromptDialog } from "./components/LogoPromptDialog";

export const CreateAdModal = ({ isOpen, onOpenChange }: CreateAdModalProps) => {
  const { t } = useTranslation("recruit");
  const [promptStep, setPromptStep] = useState(1);
  const [jobDescription, setJobDescription] = useState("");
  const [descriptionOfRoleRaw, setDescriptionOfRoleRaw] = useState("");
  const [roleTitle, setRoleTitle] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [salary, setSalary] = useState("");
  const [location, setLocation] = useState("Stockholm");
  const [isRemote, setIsRemote] = useState(false);
  const [experience, setExperience] = useState("0+ år");
  const [formOfEmployment, setFormOfEmployment] = useState("Heltid");
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [showEditDescriptionDialog, setShowEditDescriptionDialog] =
    useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [selectedBenefits, setSelectedBenefits] = useState<string[]>([]);
  const [swedishLevel, setSwedishLevel] = useState("Nybörjare");
  const [englishLevel, setEnglishLevel] = useState("Nybörjare");
  const [driversLicense, setDriversLicense] = useState("Nej");
  const [payTransparency, setPayTransparency] = useState(false);

  // New state for two-stage process
  const [hasAiExpanded, setHasAiExpanded] = useState(false);
  const [isExpandingWithAi, setIsExpandingWithAi] = useState(false);
  // Vault documents the introduction was grounded in, if any.
  const [aiSourceDocuments, setAiSourceDocuments] = useState<string[]>([]);

  // Logo prompt state
  const [showLogoPrompt, setShowLogoPrompt] = useState(false);
  const [hasCheckedLogo, setHasCheckedLogo] = useState(false);

  const { toast } = useToast();
  const { businessProfile } = useProfile();
  const { createAd, isProcessing: isCreatingAd } = useCreateAd(businessProfile);
  const {
    questions: qualifyingQuestions,
    setQuestions: setQualifyingQuestions,
    isGenerating: isGeneratingQuestions,
    generateQuestions,
    sourceDocuments: questionSourceDocuments,
  } = useQualifyingQuestions();

  // Check for logo when modal opens
  useEffect(() => {
    const checkForLogo = async () => {
      if (isOpen && businessProfile?.uid && !hasCheckedLogo) {
        try {
          await getDownloadURL(
            ref(storage, `businessUsersAvatars/${businessProfile.uid}/profile_image`)
          );
          // Logo exists, don't show prompt
          setShowLogoPrompt(false);
          setHasCheckedLogo(true);
        } catch (error) {
          // No logo exists, show prompt
          setShowLogoPrompt(true);
          setHasCheckedLogo(true);
        }
      }
    };

    checkForLogo();
  }, [isOpen, businessProfile?.uid, hasCheckedLogo]);

  // Reset form when modal is closed
  useEffect(() => {
    if (!isOpen) {
      resetForm();
      setHasCheckedLogo(false);
      setShowLogoPrompt(false);
    }
  }, [isOpen]);

  // Keyboard shortcut for reset (Ctrl+R)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (isOpen && (event.ctrlKey || event.metaKey) && event.key === "r") {
        event.preventDefault();
        setShowResetConfirm(true);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  const handleSalaryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSalary(e.target.value);
  };

  const handleExpandWithAi = async () => {
    setIsExpandingWithAi(true);
    try {
      const result =
        await trpcClient.jobAdAi.expandIntroduction.mutate({
          businessUid: businessProfile?.uid ?? "",
          jobDescription,
        });

      setJobDescription(result.expandedHtml);
      setDescriptionOfRoleRaw(getPlainTextFromHtml(result.expandedHtml));
      setAiSourceDocuments(result.sourceDocuments ?? []);
      setHasAiExpanded(true);
    } catch (error) {
      console.error("Error expanding job description with AI:", error);
      // If AI fails, still allow user to continue with rich text editor
      setHasAiExpanded(true);
      setDescriptionOfRoleRaw(getPlainTextFromHtml(jobDescription));
      toast({
        title: "Kunde inte skapa annonsintroduktion",
        description: "Något gick fel. Försök igen om en stund.",
        variant: "destructive",
      });
    } finally {
      setIsExpandingWithAi(false);
    }
  };

  const handleStepOneNext = async () => {
    setIsLoading(true);
    try {
      const businessUid = businessProfile?.uid ?? "";

      const analysis =
        await trpcClient.jobAdAi.analyzeDescription.mutate({
          businessUid,
          jobDescription,
        });

      setRoleTitle(analysis.roleTitle);
      setSalary(analysis.salaryMax);

      const empMapping: Record<string, string> = {
        "full-time": "Heltid",
        "part-time": "Deltid",
        contract: "Projektanställning",
        internship: "Praktik",
      };
      const empLower = analysis.employmentType.toLowerCase();
      const mappedEmp =
        Object.entries(empMapping).find(([key]) =>
          empLower.includes(key)
        )?.[1] || "Heltid";
      setFormOfEmployment(mappedEmp);

      if (analysis.location) {
        setLocation(analysis.location);
      }
      if (analysis.remoteOption !== undefined) {
        setIsRemote(analysis.remoteOption);
      }

      await generateQuestions(jobDescription, analysis.roleTitle || "", businessUid);

      setPromptStep(2);
    } catch (error) {
      console.error("Error processing job description:", error);
      setPromptStep(2);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStepTwoNext = () => {
    setPromptStep(3);
  };

  const handleStepThreeNext = () => {
    setPromptStep(4);
  };

  const handleStepFourNext = () => {
    setShowPreviewDialog(true);
  };

  const handleStepBack = () => {
    if (promptStep > 1) {
      setPromptStep(promptStep - 1);
    }
  };

  const handleStepForward = () => {
    if (promptStep < 4) {
      setPromptStep(promptStep + 1);
    }
  };

  const handleEditDescription = () => {
    setShowEditDescriptionDialog(true);
  };

  const handleSaveDescription = async (newDescription: string) => {
    setJobDescription(newDescription);
    // Update raw text when description is edited
    const rawText = getPlainTextFromHtml(newDescription);
    setDescriptionOfRoleRaw(rawText);
    await generateQuestions(newDescription, roleTitle, businessProfile?.uid ?? "");
  };

  const resetForm = () => {
    setJobDescription("");
    setDescriptionOfRoleRaw("");
    setRoleTitle("");
    setSelectedTags([]);
    setSalary("");
    setLocation("Stockholm");
    setIsRemote(false);
    setExperience("0+ år");
    setFormOfEmployment("Heltid");
    setSelectedBenefits([]);
    setSwedishLevel("Nybörjare");
    setEnglishLevel("Nybörjare");
    setDriversLicense("Nej");
    setPromptStep(1);
    setHasAiExpanded(false);
    setIsExpandingWithAi(false);
    setShowPreviewDialog(false);
    setShowEditDescriptionDialog(false);
    setShowResetConfirm(false);
    setQualifyingQuestions([]);
    setHasCheckedLogo(false);
    setShowLogoPrompt(false);
    setPayTransparency(false);
  };

  const handleLogoPromptComplete = () => {
    setShowLogoPrompt(false);
  };

  const handleCreateAd = async () => {
    const formData = {
      jobDescription,
      descriptionOfRoleRaw,
      shortJobDescription: jobDescription, // Use the main description as the short one too
      roleTitle,
      selectedTags,
      salary,
      location,
      isRemote,
      experience,
      formOfEmployment,
      qualifyingQuestions,
      selectedBenefits,
      swedishLevel,
      englishLevel,
      driversLicense,
      payTransparency,
    };

    await createAd(formData);

    // Reset form
    resetForm();
    onOpenChange(false);
  };

  const renderPromptContent = () => {
    switch (promptStep) {
      case 1:
        return (
          <StepOne
            onNext={handleStepOneNext}
            isProcessing={isLoading}
            jobDescription={jobDescription}
            descriptionOfRoleRaw={descriptionOfRoleRaw}
            onDescriptionChange={setJobDescription}
            onRawTextChange={setDescriptionOfRoleRaw}
            onBack={handleStepBack}
            onForward={handleStepForward}
            canGoBack={false}
            canGoForward={true}
            hasAiExpanded={hasAiExpanded}
            isExpandingWithAi={isExpandingWithAi}
            onExpandWithAi={handleExpandWithAi}
            aiSourceDocuments={aiSourceDocuments}
          />
        );

      case 2:
        return (
          <StepTwo
            onNext={handleStepTwoNext}
            roleTitle={roleTitle}
            onRoleTitleChange={setRoleTitle}
            jobDescription={jobDescription}
            selectedTags={selectedTags}
            onTagsChange={setSelectedTags}
            onEditDescription={handleEditDescription}
            onBack={handleStepBack}
            onForward={handleStepForward}
            canGoBack={true}
            canGoForward={true}
          />
        );

      case 3:
        return (
          <StepThree
            onNext={handleStepThreeNext}
            salary={salary}
            onSalaryChange={handleSalaryChange}
            payTransparency={payTransparency}
            onPayTransparencyChange={setPayTransparency}
            location={location}
            onLocationChange={setLocation}
            isRemote={isRemote}
            onRemoteChange={setIsRemote}
            experience={experience}
            onExperienceChange={setExperience}
            formOfEmployment={formOfEmployment}
            onFormOfEmploymentChange={setFormOfEmployment}
            selectedBenefits={selectedBenefits}
            onBenefitsChange={setSelectedBenefits}
            swedishLevel={swedishLevel}
            onSwedishLevelChange={setSwedishLevel}
            englishLevel={englishLevel}
            onEnglishLevelChange={setEnglishLevel}
            driversLicense={driversLicense}
            onDriversLicenseChange={setDriversLicense}
            onBack={handleStepBack}
            onForward={handleStepForward}
            canGoBack={true}
            canGoForward={true}
          />
        );

      case 4:
        return (
          <StepFour
            onNext={handleStepFourNext}
            qualifyingQuestions={qualifyingQuestions}
            onQuestionsChange={setQualifyingQuestions}
            isGeneratingQuestions={isGeneratingQuestions}
            questionSourceDocuments={questionSourceDocuments}
            onRegenerateQuestions={() =>
              generateQuestions(jobDescription, roleTitle, businessProfile?.uid ?? "")
            }
            onBack={handleStepBack}
            onForward={handleStepForward}
            canGoBack={true}
            canGoForward={false}
          />
        );

      default:
        return null;
    }
  };

  return (
    <>
      {/* Logo Prompt Dialog - Shows first if no logo exists */}
      {businessProfile?.uid && (
        <LogoPromptDialog
          isOpen={showLogoPrompt}
          onComplete={handleLogoPromptComplete}
          businessUid={businessProfile.uid}
        />
      )}

      {/* Main Create Ad Dialog - Only shows after logo check/prompt */}
      <Dialog open={isOpen && !showLogoPrompt} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col pr-0">
          <DialogHeader className="flex flex-row items-center justify-between flex-shrink-0 pb-0 pr-10">
            <DialogTitle>{t("ads.createModal.title")}</DialogTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowResetConfirm(true)}
              className="flex items-center gap-2"
              title={t("ads.createModal.reset")}>
              <RotateCcw className="h-4 w-4" />
              {t("ads.createModal.reset")}
            </Button>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto pr-6">
            {renderPromptContent()}
          </div>
        </DialogContent>
      </Dialog>

      <PreviewDialog
        isOpen={showPreviewDialog}
        onOpenChange={setShowPreviewDialog}
        formData={{
          jobDescription,
          descriptionOfRoleRaw,
          shortJobDescription: jobDescription, // Use the main description
          roleTitle,
          selectedTags,
          salary,
          location,
          isRemote,
          experience,
          formOfEmployment,
          qualifyingQuestions,
          selectedBenefits,
          swedishLevel,
          englishLevel,
          driversLicense,
          payTransparency,
        }}
        businessProfile={businessProfile}
        onSubmit={handleCreateAd}
        isProcessing={isCreatingAd}
      />

      <EditDescriptionDialog
        isOpen={showEditDescriptionDialog}
        onOpenChange={setShowEditDescriptionDialog}
        description={jobDescription}
        onSave={handleSaveDescription}
      />
      {/* Reset Confirmation Dialog */}
      <Dialog open={showResetConfirm} onOpenChange={setShowResetConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-yellow-100 flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <DialogTitle>{t("ads.createModal.resetTitle")}</DialogTitle>
                <DialogDescription>
                  {t("ads.createModal.resetDescription")}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="outline"
              onClick={() => setShowResetConfirm(false)}>
              {t("ads.createModal.common.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                resetForm();
                setShowResetConfirm(false);
              }}>
              {t("ads.createModal.resetConfirm")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CreateAdModal;
